/* LocationBar — always-visible top bar for per-location data isolation.
 * Admin: dropdown of all locations (checkboxes + Apply) and the current
 * selection displayed as chips across the bar (chip X removes instantly).
 * Member: read-only chips of their assigned locations.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, ChevronDown, Loader2, X, Check, Globe } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function LocationBar() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  // Fetch locations on mount AND every time the dropdown opens — the
  // latter is critical because a newly-created Location (e.g. from an
  // open-house scan against a fresh city) needs to show up in the
  // filter without forcing a full page reload.
  const fetchLocations = React.useCallback(async () => {
    try {
      const { data } = await api.get("/locations");
      setLocations(data.locations || []);
    } catch {
      // silent — the bar should never break the app shell
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Re-sync the checked set with the user's canonical active_location_ids
  // whenever the popover opens, so partially-changed local state doesn't
  // stick around after a "Cancel" (click-outside).
  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      fetchLocations();
      setSelected(new Set(user?.active_location_ids || []));
    }
    setOpen(nextOpen);
  };

  useEffect(() => {
    if (user) setSelected(new Set(user.active_location_ids || []));
  }, [user]);

  if (!user) return null;

  const activeIds = user.active_location_ids || [];
  const activeChips = locations.filter((l) => activeIds.includes(l.id));

  const persist = async (ids) => {
    setSaving(true);
    try {
      await api.put("/me/active-locations", { location_ids: ids });
      window.location.reload();
    } catch (e) {
      toast.error("Couldn't update view", { description: formatApiError(e) });
      setSaving(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const removeChip = (id) => persist(activeIds.filter((x) => x !== id));

  return (
    <div
      className="md:sticky md:top-0 z-30 flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white/95 backdrop-blur px-4 py-2 sm:px-6"
      data-testid="location-bar"
    >
      {isAdmin ? (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-2 font-medium shrink-0"
              disabled={saving}
              data-testid="location-bar-dropdown"
            >
              <MapPin className="h-4 w-4 text-slate-500" />
              Locations
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start" data-testid="location-bar-popover">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              Viewing data for
            </div>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className={`w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-left hover:bg-slate-100 ${
                selected.size === 0 ? "font-semibold text-slate-900" : "text-slate-600"
              }`}
              data-testid="location-bar-all"
            >
              <span className="h-4 w-4 flex items-center justify-center">
                {selected.size === 0 ? <Check className="h-3.5 w-3.5" /> : null}
              </span>
              All locations
            </button>
            <div className="my-1 border-t border-slate-100" />
            <div className="max-h-56 overflow-y-auto space-y-0.5">
              {locations.map((loc) => (
                <label
                  key={loc.id}
                  className="flex items-center gap-2 rounded-md px-2 py-2 text-sm cursor-pointer hover:bg-slate-100"
                >
                  <Checkbox
                    checked={selected.has(loc.id)}
                    onCheckedChange={() => toggle(loc.id)}
                    data-testid={`location-bar-checkbox-${loc.id}`}
                  />
                  <span className="truncate text-slate-800">{loc.name}</span>
                </label>
              ))}
              {locations.length === 0 ? (
                <div className="text-xs text-slate-500 px-2 py-2">
                  No locations yet — create one on the Locations page.
                </div>
              ) : null}
            </div>
            <Button
              onClick={() => persist(Array.from(selected))}
              disabled={saving}
              size="sm"
              className="w-full mt-3 min-h-[38px] text-white"
              style={{ background: "hsl(var(--brand))" }}
              data-testid="location-bar-apply"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Apply view
            </Button>
          </PopoverContent>
        </Popover>
      ) : (
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500 shrink-0">
          <MapPin className="h-3.5 w-3.5" /> Your location{(locations.length > 1) ? "s" : ""}
        </span>
      )}

      {/* Selected location chips — always visible */}
      <div className="flex flex-wrap items-center gap-1.5 min-w-0" data-testid="location-bar-chips">
        {isAdmin && activeChips.length === 0 ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-medium"
            data-testid="location-chip-all"
          >
            <Globe className="h-3 w-3" /> All locations
          </span>
        ) : null}
        {isAdmin
          ? activeChips.map((loc) => (
              <span
                key={loc.id}
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 pl-3 pr-1.5 py-1 text-xs font-medium text-slate-800"
                data-testid={`location-chip-${loc.id}`}
              >
                {loc.name}
                <button
                  type="button"
                  onClick={() => removeChip(loc.id)}
                  disabled={saving}
                  className="rounded-full p-0.5 hover:bg-slate-300 text-slate-500 hover:text-slate-800"
                  title={`Stop viewing ${loc.name}`}
                  data-testid={`location-chip-remove-${loc.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          : locations.map((loc) => (
              <span
                key={loc.id}
                className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-800"
                data-testid={`location-chip-${loc.id}`}
              >
                {loc.name}
              </span>
            ))}
        {!isAdmin && locations.length === 0 ? (
          <span className="text-xs text-slate-500">None assigned — ask an admin</span>
        ) : null}
      </div>
    </div>
  );
}
